import { useState, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { GratuityLeadForm } from './GratuityLeadForm';

const fmt = (n) => `SAR ${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* Interactive ESG Calculator Engine — Article 84/85 statutory logic */
export const GratuityCalculatorWidget = () => {
  const [reason, setReason] = useState('termination');
  const [basic, setBasic] = useState(8000);
  const [allowances, setAllowances] = useState(4000);
  const [years, setYears] = useState(6);
  const [months, setMonths] = useState(0);

  const r = useMemo(() => {
    const sf = (Number(basic) || 0) + (Number(allowances) || 0);
    const mTotal = (Number(years) || 0) * 12 + (Number(months) || 0);
    const m1 = Math.min(mTotal, 60);
    const m2 = Math.max(0, mTotal - 60);
    const tier1 = m1 * 0.5 * (sf / 12);
    const tier2 = m2 * 1.0 * (sf / 12);
    const gBase = tier1 + tier2;
    let ra;
    if (reason === 'termination') {
      ra = mTotal >= 12 ? 1 : 0;
    } else if (mTotal < 24) ra = 0;
    else if (mTotal < 60) ra = 1 / 3;
    else if (mTotal < 120) ra = 2 / 3;
    else ra = 1;
    return { sf, mTotal, tier1, tier2, gBase, ra, payout: ra * gBase };
  }, [reason, basic, allowances, years, months]);

  const numInput = (label, value, setter, testId, note) => (
    <div>
      <label className="block text-xs font-bold text-[#000080] uppercase tracking-wide mb-1.5">{label}</label>
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => setter(e.target.value)}
        data-testid={testId}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#38CAFC]"
        style={{ colorScheme: 'light' }}
      />
      {note && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{note}</p>}
    </div>
  );

  return (
    <div className="rounded-2xl border-2 border-[#000080]/15 bg-gradient-to-br from-slate-50 to-blue-50/50 p-6 sm:p-8 shadow-md my-10" data-testid="gratuity-calculator-widget">
      <h2 className="text-xl sm:text-2xl font-bold text-[#000080] font-syne mb-6">Interactive End-of-Service Gratuity Calculator (Article 84 & 85)</h2>

      <div className="mb-6">
        <span className="block text-xs font-bold text-[#000080] uppercase tracking-wide mb-2">Contract Termination Reason</span>
        <div className="flex flex-col sm:flex-row gap-3">
          {[
            { v: 'termination', label: 'Employer Termination / Non-Renewal / Force Majeure', tid: 'esg-reason-termination' },
            { v: 'resignation', label: 'Resignation (Article 85 Scaling Applies)', tid: 'esg-reason-resignation' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setReason(o.v)}
              data-testid={o.tid}
              className={`flex-1 text-left text-sm font-semibold rounded-lg border-2 px-4 py-3 transition-colors ${
                reason === o.v ? 'border-[#000080] bg-[#000080] text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-[#000080]/50'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-8">
        {numInput('Basic Monthly Salary (SAR)', basic, setBasic, 'esg-input-basic')}
        {numInput('Fixed Housing & Transport Allowances (SAR)', allowances, setAllowances, 'esg-input-allowances',
          'Under Article 84, fixed housing and transportation allowances MUST be included in the calculation base.')}
        {numInput('Years of Service (0–40)', years, setYears, 'esg-input-years')}
        {numInput('Additional Months (0–11)', months, setMonths, 'esg-input-months')}
      </div>

      <div className="bg-[#0A192F] rounded-xl p-6 text-white mb-5" data-testid="esg-result-card">
        <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">Total Estimated Statutory Payout</p>
        <p className="text-3xl sm:text-4xl font-bold font-syne text-[#38CAFC]" data-testid="esg-total-payout">{fmt(r.payout)}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5 text-sm">
        <div className="bg-white rounded-lg border border-slate-200 p-4"><p className="text-[11px] text-slate-500 mb-1">Base Salary Considered (S<sub>f</sub>)</p><p className="font-bold text-[#000080]" data-testid="esg-breakdown-sf">{fmt(r.sf)}</p></div>
        <div className="bg-white rounded-lg border border-slate-200 p-4"><p className="text-[11px] text-slate-500 mb-1">Gratuity — First 5 Years (Tier 1)</p><p className="font-bold text-[#000080]" data-testid="esg-breakdown-tier1">{fmt(r.tier1)}</p></div>
        <div className="bg-white rounded-lg border border-slate-200 p-4"><p className="text-[11px] text-slate-500 mb-1">Gratuity — Subsequent Years (Tier 2)</p><p className="font-bold text-[#000080]" data-testid="esg-breakdown-tier2">{fmt(r.tier2)}</p></div>
        <div className="bg-white rounded-lg border border-slate-200 p-4"><p className="text-[11px] text-slate-500 mb-1">Applied Settlement Percentage (R<sub>a</sub>)</p><p className="font-bold text-[#000080]" data-testid="esg-breakdown-ra">{(r.ra * 100).toFixed(2).replace(/\.00$/, '')}%</p></div>
      </div>

      <div className="flex items-start gap-3 bg-[#FFF3CD] border-l-4 border-[#FFC107] rounded-r-lg p-4 text-xs text-slate-800 leading-relaxed" data-testid="esg-mudad-warning">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
        <p><strong>Disclaimer:</strong> Final settlements must be logged on Mudad Wage Protection System within 7–14 days of contract end to avoid Nitaqat rating penalties.</p>
      </div>

      <GratuityLeadForm result={r} reason={reason} basic={basic} allowances={allowances} years={years} months={months} />
    </div>
  );
};
